import { createReducer } from 'redux-create-reducer';
import { promiseAction, fetchApi } from '../utils';
import { UserManager } from 'oidc-client-ts';

const AUTHORIZATION_START = 'USER/AUTHORIZATION_START';
const AUTHORIZATION_SUCCESS = 'USER/AUTHORIZATION_SUCCESS';
const AUTHORIZATION_ERROR = 'USER/AUTHORIZATION_ERROR';

const VALIDATE_USER_START = 'USER/VALIDATE_USER_START';
const VALIDATE_USER_SUCCESS = 'USER/VALIDATE_USER_SUCCESS';
const VALIDATE_USER_ERROR = 'USER/VALIDATE_USER_ERROR';

export const initialState = {
  isFetching: false,
  error: '',
  tokens: {},
  okta: undefined,
};

export default createReducer(initialState, {
  [AUTHORIZATION_START]: (state) => ({
    ...state,
    error: '',
    isFetching: true,
  }),
  [AUTHORIZATION_SUCCESS]: (state, { payload }) => ({
    ...state,
    ...payload,
    isFetching: false,
  }),
  [AUTHORIZATION_ERROR]: (state, { error }) => ({
    ...state,
    error,
  }),
  [VALIDATE_USER_SUCCESS]: (state, { payload }) => {
    return {
      ...state,
      tokens: { idToken: payload.tokens.idToken },
    };
  },
  [VALIDATE_USER_ERROR]: (state) => ({
    ...state,
    tokens: initialState.tokens,
  }),
});

export const getIdToken = (state) => state.user.tokens.idToken;
export const isAuthenticated = (state) => !!getIdToken(state);
export const getOkta = (state) => state.user.okta;

const removeCodeAndState = () => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  urlSearchParams.delete('code');
  urlSearchParams.delete('state');
  const queryString = urlSearchParams.toString();
  const newUrl = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname;
  window.history.replaceState({}, document.title, newUrl);
};

const validateUser = async (getState) => {
  const state = getState();
  const auth = await getOkta(state);

  try {
    // Try to log in, if there is a code in the URL
    if (
      window.location.search.includes('code=') &&
      window.location.search.includes('state=')
    ) {
      await auth.signinCallback().then(async () => {
        const user = await auth.getUser();
        removeCodeAndState();
        if (user) {
          return Promise.resolve({
            tokens: {
              idToken: user.id_token,
            },
          });
        }
        return Promise.reject();
      });
    }

    // Check if we have an active user
    const user = await auth.getUser();
    // Refresh if needed
    if (user && user.expires_at !== undefined) {
      const currentTime = Math.floor(Date.now() / 1000);
      const expired = user.expires_at < currentTime;
      if (expired) {
        return Promise.reject();
      }
      return Promise.resolve({
        tokens: {
          idToken: user.id_token,
        },
      });
    }
    return Promise.reject();
  } catch (e) {
    return Promise.reject();
  }
};

export const getAuthorizationHeaders = (state) => {
  return {
    headers: {
      Authorization: `Bearer ${getIdToken(state)}`,
    },
  };
};

const initialize = async () => {
  const { data: config } = await fetchApi({ url: 'api/v1/auth/config' });
  return {
    okta: new UserManager({
      authority: config.authority,
      client_id: config.clientId,
      redirect_uri: config.redirectUrl,
      scope: config.scopes,
      metadataUrl: config.metadataUrl,
      automaticSilentRenew: false,
    }),
  };
  /*
    const authentication = new auth0.WebAuth({
      clientID: config.clientID,
      domain: config.domain,
      responseType: 'id_token',
      redirectUri: config.redirectUri,
      audience: config.audience,
      scope: 'openid email username',
    });

    return {
      auth0: {
        login: () => authentication.authorize(),
        checkSession: withPromsie((cb) =>
          authentication.checkSession({ responseType: 'id_token' }, cb)
        ),
        parseHash: withPromsie((cb) =>
          authentication.parseHash({ responseType: 'id_token' }, cb)
        ),
      },
    };

   */
};

export const initializeUserAction = () => async (dispatch) => {
  try {
    await dispatch(
      promiseAction({
        types: [
          AUTHORIZATION_START,
          AUTHORIZATION_SUCCESS,
          AUTHORIZATION_ERROR,
        ],
        promise: initialize,
      })
    );

    await dispatch(
      promiseAction({
        types: [
          VALIDATE_USER_START,
          VALIDATE_USER_SUCCESS,
          VALIDATE_USER_ERROR,
        ],
        promise: validateUser,
      })
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const loginAction = () => async (dispatch, getState) => {
  const okta = await getOkta(getState());
  return okta.signinRedirect();
};

export const logoutAction = () => (dispatch, getState) => {
  const okta = getOkta(getState());
  try {
    okta.signoutSilent();
  } catch (err) {}
  window.localStorage.clear();
};
